import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { StatusBadge, SaleCountdown } from "@/components/bits";
import { productImage, isCustomImage, LOGO } from "@/lib/images";

const CAT_LABEL = { cheat: "CHEAT", account: "ACCOUNT", spoofer: "SPOOFER" };

export default function ProductCard({ p, index = 0 }) {
  const custom = isCustomImage(p.image);
  const saleTiers = (p.tiers || []).filter((t) => t.sale_price != null);
  const onSale = saleTiers.length > 0;
  const maxSale = onSale ? Math.max(...saleTiers.map((t) => t.sale_pct || p.sale_pct || 0)) : 0;
  const uniformSale = onSale && saleTiers.length === (p.tiers || []).length && saleTiers.every((t) => (t.sale_pct || 0) === maxSale);
  const saleLabel = uniformSale ? `SALE -${maxSale}%` : `SALE UP TO -${maxSale}%`;
  const saleMin = onSale ? (p.sale_min_price ?? Math.min(...saleTiers.map((t) => t.sale_price))) : null;
  const cardRef = useRef(null);
  const sheenRef = useRef(null);

  const tilt = (e) => {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const rx = (0.5 - y) * 8;
    const ry = (x - 0.5) * 8;
    card.style.transform = `perspective(1000px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-3px)`;
    if (sheenRef.current) {
      sheenRef.current.style.opacity = "0.28";
      sheenRef.current.style.background = `radial-gradient(circle at ${x * 100}% ${y * 100}%, rgba(255,255,255,0.34), rgba(0,240,255,0.12) 28%, transparent 58%)`;
    }
  };

  const resetTilt = () => {
    if (cardRef.current) cardRef.current.style.transform = "";
    if (sheenRef.current) sheenRef.current.style.opacity = "0";
  };
  return (
    <Link
      to={`/product/${p.slug}`}
      data-testid={`product-card-${p.slug}`}
      ref={cardRef}
      onMouseMove={tilt}
      onMouseLeave={resetTilt}
      className="group card-inj tilt-card block relative"
    >
      {/* TARGET LOCKED corner brackets (hover) */}
      <div className="absolute inset-0 z-10 pointer-events-none" data-testid={`target-lock-${p.slug}`}>
        {["-top-px -left-px border-t-2 border-l-2", "-top-px -right-px border-t-2 border-r-2", "-bottom-px -left-px border-b-2 border-l-2", "-bottom-px -right-px border-b-2 border-r-2"].map((cls) => (
          <span key={cls} className={`absolute w-6 h-6 border-white opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300 ${cls}`} />
        ))}
      </div>
      <div ref={sheenRef} className="tilt-sheen" data-testid={`product-tilt-sheen-${p.slug}`} />
      <div className="relative h-52 overflow-hidden border-b border-white/10 bg-black">
        <img
          src={productImage(p.image)} alt={p.name} className="h-full w-full object-cover"
          style={custom ? undefined : { filter: "grayscale(100%) brightness(45%) contrast(120%)" }}
        />
        {!custom && (
          <>
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <img src={LOGO} alt="" className="h-16 w-16 object-contain opacity-85 transition-transform duration-500 group-hover:scale-110" style={{ filter: "drop-shadow(0 0 22px rgba(255,255,255,0.3))" }} />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
          </>
        )}
        <div className="absolute top-3 left-3 flex flex-col items-start gap-2">
          <StatusBadge status={p.status} small />
          {p.days_undetected > 0 && (
            <span className="font-mono text-[9px] tracking-[0.2em] rounded-full border border-white/20 bg-black/60 px-2 py-1 text-white/70" data-testid={`streak-${p.slug}`}>
              {p.days_undetected}D UNDETECTED
            </span>
          )}
        </div>
        {p.sold_out && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70">
            <span className="font-mono text-xs tracking-[0.3em] rounded-full border border-white/40 px-4 py-2">RESTOCKING</span>
          </div>
        )}
        <div className="absolute top-3 right-3 font-mono text-[10px] tracking-[0.2em] text-white/50 rounded-full border border-white/15 px-2 py-1 bg-black/40">
          {CAT_LABEL[p.category]}
        </div>
        <div className="absolute bottom-3 left-3 font-mono text-[10px] tracking-[0.25em] text-white/40">
          {String(index + 1).padStart(2, "0")}
        </div>
        {onSale && (
          <div className="absolute bottom-3 right-3 font-mono text-[10px] tracking-[0.2em] rounded-full bg-white text-black px-2 py-1" data-testid={`sale-badge-${p.slug}`}>
            {saleLabel}
          </div>
        )}
      </div>

      <div className="p-6">
        <div className="font-mono text-[10px] tracking-[0.25em] text-white/40 mb-2">{p.game}</div>
        <h3 className="font-display text-2xl uppercase leading-none mb-3">{p.name}</h3>
        <p className="text-sm text-white/50 leading-relaxed line-clamp-2 min-h-[40px]">{p.tagline}</p>

        <div className="mt-5 flex items-end justify-between">
          <div>
            <div className="font-mono text-[10px] tracking-[0.2em] text-white/40">FROM</div>
            {onSale && (
              <div className="font-mono text-sm text-white/35 line-through leading-none mb-1">${p.from_price.toFixed(2)}</div>
            )}
            <div className="font-display text-3xl leading-none" data-testid={`card-price-${p.slug}`}>${(saleMin ?? p.from_price).toFixed(2)}</div>
            {p.manage_stock && p.stock > 0 && (
              <div className="mt-1.5 font-mono text-[10px] tracking-[0.2em] text-white/40" data-testid={`stock-count-${p.slug}`}>
                {p.stock} IN STOCK
              </div>
            )}
            {p.vouch_count > 0 && (
              <div className="mt-1.5 font-mono text-[10px] tracking-[0.2em] text-white/55" data-testid={`vouch-count-${p.slug}`}>
                ★ VOUCHED ×{p.vouch_count}
              </div>
            )}
            {onSale && p.sale_ends_at && (
              <div className="mt-2"><SaleCountdown endsAt={p.sale_ends_at} tid={`card-${p.slug}`} /></div>
            )}
          </div>
          <span className="flex items-center gap-1 font-mono text-xs tracking-[0.15em] text-white/60 group-hover:text-white transition-colors">
            VIEW <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}
